import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useBasket } from '@/contexts/basketContext.tsx'
import { Loading, Card, Input, Select, Checkbox } from 'react-daisyui'
import { Button } from '@/components/Button.tsx'
import { IconArrowBack, IconShoppingCart } from '@tabler/icons-react'
import { useClient } from '@/stores/useClient.ts'
import { useProfile } from '@/stores/useAuth.ts'
import { CheckoutRequest } from '@/models/shop.ts'

export default function CheckoutPage() {
  const navigate = useNavigate()
  const { driveId } = useParams<{ driveId: string }>()
  const { basket, isLoading } = useBasket()
  const client = useClient()
  const { profile } = useProfile()
  const needsEmail = !profile?.email
  const [isProcessing, setIsProcessing] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const CHECKOUT_STORAGE_KEY = 'checkoutFormData'
  const CHECKOUT_BILLING_DIFFERENT_KEY = 'checkoutBillingDifferent'

  type CheckoutFormData = {
    email: string
    shipping_first_name: string
    shipping_last_name: string
    shipping_address_line1: string
    shipping_address_line2: string
    shipping_city: string
    shipping_postal_code: string
    shipping_country: string
    shipping_phone: string
    billing_first_name: string
    billing_last_name: string
    billing_address_line1: string
    billing_address_line2: string
    billing_city: string
    billing_postal_code: string
    billing_country: string
    billing_phone: string
    customer_notes: string
  }

  const defaultFormData: CheckoutFormData = {
    email: '',
    shipping_first_name: '',
    shipping_last_name: '',
    shipping_address_line1: '',
    shipping_address_line2: '',
    shipping_city: '',
    shipping_postal_code: '',
    shipping_country: 'DE',
    shipping_phone: '',
    billing_first_name: '',
    billing_last_name: '',
    billing_address_line1: '',
    billing_address_line2: '',
    billing_city: '',
    billing_postal_code: '',
    billing_country: 'DE',
    billing_phone: '',
    customer_notes: '',
  }

  const [formData, setFormData] = useState<CheckoutFormData>(() => {
    try {
      const stored = localStorage.getItem(CHECKOUT_STORAGE_KEY)
      if (stored) {
        return { ...defaultFormData, ...JSON.parse(stored) }
      }
    } catch (e) {
      console.error('Failed to read checkout form from localStorage', e)
    }
    return defaultFormData
  })

  const [billingDifferent, setBillingDifferent] = useState(() => {
    return localStorage.getItem(CHECKOUT_BILLING_DIFFERENT_KEY) === 'true'
  })

  useEffect(() => {
    try {
      localStorage.setItem(CHECKOUT_STORAGE_KEY, JSON.stringify(formData))
    } catch (e) {
      console.error('Failed to persist checkout form to localStorage', e)
    }
  }, [formData])

  useEffect(() => {
    localStorage.setItem(CHECKOUT_BILLING_DIFFERENT_KEY, billingDifferent ? 'true' : 'false')
  }, [billingDifferent])

  const shippingCost = ({
    'DE': '4.99',
    'AT': '5.99',
  } as Record<string, string>)[formData.shipping_country] ?? '4.99'

  const formatPrice = (price: string) => {
    return `€${parseFloat(price).toFixed(2)}`
  }

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (needsEmail) {
      const email = formData.email.trim()
      if (!email) {
        newErrors.email = 'E-Mail ist erforderlich'
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        newErrors.email = 'Bitte gültige E-Mail-Adresse eingeben'
      }
    }

    // Shipping validation
    if (!formData.shipping_first_name.trim()) {
      newErrors.shipping_first_name = 'Vorname ist erforderlich'
    }
    if (!formData.shipping_last_name.trim()) {
      newErrors.shipping_last_name = 'Nachname ist erforderlich'
    }
    if (!formData.shipping_address_line1.trim()) {
      newErrors.shipping_address_line1 = 'Adresse ist erforderlich'
    }
    if (!formData.shipping_city.trim()) {
      newErrors.shipping_city = 'Stadt ist erforderlich'
    }
    if (!formData.shipping_postal_code.trim()) {
      newErrors.shipping_postal_code = 'Postleitzahl ist erforderlich'
    }

    // Billing validation if different
    if (billingDifferent) {
      if (!formData.billing_first_name.trim()) {
        newErrors.billing_first_name = 'Vorname ist erforderlich'
      }
      if (!formData.billing_last_name.trim()) {
        newErrors.billing_last_name = 'Nachname ist erforderlich'
      }
      if (!formData.billing_address_line1.trim()) {
        newErrors.billing_address_line1 = 'Adresse ist erforderlich'
      }
      if (!formData.billing_city.trim()) {
        newErrors.billing_city = 'Stadt ist erforderlich'
      }
      if (!formData.billing_postal_code.trim()) {
        newErrors.billing_postal_code = 'Postleitzahl ist erforderlich'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleCheckout = async () => {
    if (!validateForm()) {
      return
    }

    try {
      setIsProcessing(true)

      const checkoutData: CheckoutRequest = {
        basket_id: basket!.id,
        shipping_first_name: formData.shipping_first_name,
        shipping_last_name: formData.shipping_last_name,
        shipping_address_line1: formData.shipping_address_line1,
        shipping_address_line2: formData.shipping_address_line2,
        shipping_city: formData.shipping_city,
        shipping_postal_code: formData.shipping_postal_code,
        shipping_country: formData.shipping_country,
        shipping_phone: formData.shipping_phone,
        customer_notes: formData.customer_notes,
      }

      if (needsEmail) {
        checkoutData.email = formData.email.trim()
      }

      if (billingDifferent) {
        Object.assign(checkoutData, {
          billing_first_name: formData.billing_first_name,
          billing_last_name: formData.billing_last_name,
          billing_address_line1: formData.billing_address_line1,
          billing_address_line2: formData.billing_address_line2,
          billing_city: formData.billing_city,
          billing_postal_code: formData.billing_postal_code,
          billing_country: formData.billing_country,
          billing_phone: formData.billing_phone,
        })
      }

      const response = await client.checkout(checkoutData)

      if (response.data.checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.checkout_url
      } else {
        alert('Fehler beim Erstellen der Checkout-Sitzung')
      }
    } catch (error) {
      console.error('Checkout error:', error)
      alert('Fehler beim Checkout-Prozess')
    } finally {
      setIsProcessing(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loading color="primary" />
      </div>
    )
  }

  if (!basket || basket.items.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4">
          <IconShoppingCart size={80} className="text-gray-400" />
          <h2 className="text-xl font-semibold text-gray-600">Ihr Warenkorb ist leer</h2>
          <Button
            color="primary"
            size="lg"
            onClick={() => navigate(`/d/${driveId}/shop`)}
          >
            Zum Shop
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <Button
          color="primary"
          variant="outline"
          size="sm"
          onClick={() => navigate(`/d/${driveId}/shop/basket`)}
        >
          <IconArrowBack size={20} />
          Zurück zum Warenkorb
        </Button>

        <h1 className="text-2xl font-bold">Kasse</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Form Section */}
        <div className="lg:col-span-2 space-y-6">
          {needsEmail && (
            <Card className="bg-base-100 shadow-lg">
              <div className="card-body">
                <h2 className="card-title mb-4">Kontakt</h2>
                <Input
                  type="email"
                  placeholder="E-Mail-Adresse"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  color={errors.email ? 'error' : undefined}
                  disabled={isProcessing}
                />
                {errors.email && (
                  <span className="text-error text-sm mt-1">{errors.email}</span>
                )}
              </div>
            </Card>
          )}

          {/* Shipping Information */}
          <Card className="bg-base-100 shadow-lg">
            <div className="card-body">
              <h2 className="card-title mb-4">Versandadresse</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Input
                    type="text"
                    placeholder="Vorname"
                    value={formData.shipping_first_name}
                    onChange={(e) => handleInputChange('shipping_first_name', e.target.value)}
                    color={errors.shipping_first_name ? 'error' : undefined}
                    disabled={isProcessing}
                  />
                  {errors.shipping_first_name && (
                    <span className="text-error text-sm mt-1">{errors.shipping_first_name}</span>
                  )}
                </div>

                <div>
                  <Input
                    type="text"
                    placeholder="Nachname"
                    value={formData.shipping_last_name}
                    onChange={(e) => handleInputChange('shipping_last_name', e.target.value)}
                    color={errors.shipping_last_name ? 'error' : undefined}
                    disabled={isProcessing}
                  />
                  {errors.shipping_last_name && (
                    <span className="text-error text-sm mt-1">{errors.shipping_last_name}</span>
                  )}
                </div>

                <div className="md:col-span-2">
                  <Input
                    type="text"
                    placeholder="Straße + Hausnummer"
                    value={formData.shipping_address_line1}
                    onChange={(e) => handleInputChange('shipping_address_line1', e.target.value)}
                    color={errors.shipping_address_line1 ? 'error' : undefined}
                    disabled={isProcessing}
                  />
                  {errors.shipping_address_line1 && (
                    <span className="text-error text-sm mt-1">{errors.shipping_address_line1}</span>
                  )}
                </div>

                <div className="md:col-span-2">
                  <Input
                    type="text"
                    placeholder="Adresszusatz (optional)"
                    value={formData.shipping_address_line2}
                    onChange={(e) => handleInputChange('shipping_address_line2', e.target.value)}
                    disabled={isProcessing}
                  />
                </div>

                <div>
                  <Input
                    type="text"
                    placeholder="Stadt"
                    value={formData.shipping_city}
                    onChange={(e) => handleInputChange('shipping_city', e.target.value)}
                    color={errors.shipping_city ? 'error' : undefined}
                    disabled={isProcessing}
                  />
                  {errors.shipping_city && (
                    <span className="text-error text-sm mt-1">{errors.shipping_city}</span>
                  )}
                </div>

                <div>
                  <Input
                    type="text"
                    placeholder="Postleitzahl"
                    value={formData.shipping_postal_code}
                    onChange={(e) => handleInputChange('shipping_postal_code', e.target.value)}
                    color={errors.shipping_postal_code ? 'error' : undefined}
                    disabled={isProcessing}
                  />
                  {errors.shipping_postal_code && (
                    <span className="text-error text-sm mt-1">{errors.shipping_postal_code}</span>
                  )}
                </div>

                <div>
                  <Select
                    value={formData.shipping_country}
                    onChange={(e) => handleInputChange('shipping_country', e.target.value)}
                    disabled={isProcessing}
                  >
                    <option value="DE">Deutschland</option>
                    <option value="AT">Österreich</option>
                  </Select>
                </div>

                <div>
                  <Input
                    type="tel"
                    placeholder="Telefonnummer (optional)"
                    value={formData.shipping_phone}
                    onChange={(e) => handleInputChange('shipping_phone', e.target.value)}
                    disabled={isProcessing}
                  />
                </div>
              </div>
            </div>
          </Card>

          {/* Billing Information */}
          <Card className="bg-base-100 shadow-lg">
            <div className="card-body">
              <div className="flex items-center gap-2 mb-4">
                <Checkbox
                  checked={billingDifferent}
                  onChange={(e) => setBillingDifferent(e.target.checked)}
                  disabled={isProcessing}
                />
                <label className="cursor-pointer">Rechnungsadresse weicht von Lieferadresse ab</label>
              </div>

              {billingDifferent && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Input
                      type="text"
                      placeholder="Vorname"
                      value={formData.billing_first_name}
                      onChange={(e) => handleInputChange('billing_first_name', e.target.value)}
                      color={errors.billing_first_name ? 'error' : undefined}
                      disabled={isProcessing}
                    />
                    {errors.billing_first_name && (
                      <span className="text-error text-sm mt-1">{errors.billing_first_name}</span>
                    )}
                  </div>

                  <div>
                    <Input
                      type="text"
                      placeholder="Nachname"
                      value={formData.billing_last_name}
                      onChange={(e) => handleInputChange('billing_last_name', e.target.value)}
                      color={errors.billing_last_name ? 'error' : undefined}
                      disabled={isProcessing}
                    />
                    {errors.billing_last_name && (
                      <span className="text-error text-sm mt-1">{errors.billing_last_name}</span>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <Input
                      type="text"
                      placeholder="Straße + Hausnummer"
                      value={formData.billing_address_line1}
                      onChange={(e) => handleInputChange('billing_address_line1', e.target.value)}
                      color={errors.billing_address_line1 ? 'error' : undefined}
                      disabled={isProcessing}
                    />
                    {errors.billing_address_line1 && (
                      <span className="text-error text-sm mt-1">{errors.billing_address_line1}</span>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <Input
                      type="text"
                      placeholder="Adresszusatz (optional)"
                      value={formData.billing_address_line2}
                      onChange={(e) => handleInputChange('billing_address_line2', e.target.value)}
                      disabled={isProcessing}
                    />
                  </div>

                  <div>
                    <Input
                      type="text"
                      placeholder="Stadt"
                      value={formData.billing_city}
                      onChange={(e) => handleInputChange('billing_city', e.target.value)}
                      color={errors.billing_city ? 'error' : undefined}
                      disabled={isProcessing}
                    />
                    {errors.billing_city && (
                      <span className="text-error text-sm mt-1">{errors.billing_city}</span>
                    )}
                  </div>

                  <div>
                    <Input
                      type="text"
                      placeholder="Postleitzahl"
                      value={formData.billing_postal_code}
                      onChange={(e) => handleInputChange('billing_postal_code', e.target.value)}
                      color={errors.billing_postal_code ? 'error' : undefined}
                      disabled={isProcessing}
                    />
                    {errors.billing_postal_code && (
                      <span className="text-error text-sm mt-1">{errors.billing_postal_code}</span>
                    )}
                  </div>

                  <div>
                    <Select
                      value={formData.billing_country}
                      onChange={(e) => handleInputChange('billing_country', e.target.value)}
                      disabled={isProcessing}
                    >
                      <option value="DE">Deutschland</option>
                      <option value="AT">Österreich</option>
                    </Select>
                  </div>

                  <div>
                    <Input
                      type="tel"
                      placeholder="Telefonnummer (optional)"
                      value={formData.billing_phone}
                      onChange={(e) => handleInputChange('billing_phone', e.target.value)}
                      disabled={isProcessing}
                    />
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Additional Notes */}
          <Card className="bg-base-100 shadow-lg">
            <div className="card-body">
              <h2 className="card-title mb-4">Zusätzliche Informationen</h2>
              <textarea
                className="textarea textarea-bordered w-full"
                rows={3}
                placeholder="Anmerkungen zur Bestellung (optional)"
                value={formData.customer_notes}
                onChange={(e) => handleInputChange('customer_notes', e.target.value)}
                disabled={isProcessing}
              />
            </div>
          </Card>
        </div>

        {/* Order Summary */}
        <div className="lg:col-span-1">
          <Card className="bg-base-100 shadow-lg sticky top-4">
            <div className="card-body">
              <h2 className="card-title mb-4">Bestellübersicht</h2>

              <div className="space-y-3 mb-4">
                {basket.items.map((item) => (
                  <div key={item.id} className="flex justify-between text-sm">
                    <div className="flex-1">
                      <div className="font-medium">
                        {item.product.title}
                        {item.variation && ` - ${item.variation.title_suffix}`}
                      </div>
                      <div className="text-gray-500">
                        Menge: {item.quantity} × {item.units} Stück
                      </div>
                    </div>
                    <div className="text-right">
                      {formatPrice(item.total_price)}
                    </div>
                  </div>
                ))}
              </div>

              <hr className="my-4" />

              <div className="space-y-2">
                <div className="flex justify-between">
                  <span>Zwischensumme:</span>
                  <span>{formatPrice(basket.total_price)}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Versand (1-2 Wochen):</span>
                  <span>{formatPrice(shippingCost)}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-600">
                  <span>MwSt.:</span>
                  <span>Inklusive</span>
                </div>
                <hr className="my-2" />
                <div className="flex justify-between font-bold text-lg">
                  <span>Gesamt:</span>
                  <span>{formatPrice('' + (parseFloat(basket.total_price) + parseFloat(shippingCost)))}</span>
                </div>
              </div>

              <Button
                color="primary"
                size="lg"
                className="w-full mt-6"
                onClick={handleCheckout}
                disabled={isProcessing || basket.items.length === 0}
              >
                {isProcessing ? (
                  <>
                    <Loading size="sm" />
                    Verarbeitung...
                  </>
                ) : (
                  'Zur Zahlung'
                )}
              </Button>

              <p className="text-xs text-gray-500 text-center mt-4">
                Sie werden zu Stripe weitergeleitet, um die Zahlung sicher abzuschließen.
              </p>
            </div>
          </Card>
        </div>
      </div>
    </div>
  )
}
